import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Button } from "../ui/atoms/Button";

const starValues = [1, 2, 3, 4, 5];

type CandidateFeedbackModalVariant = "form" | "success" | "admin";

interface CandidateFeedbackModalProps {
    isOpen: boolean;
    variant: CandidateFeedbackModalVariant;
    rating: number | null;
    comment: string;
    candidate: string;
    isSubmitting: boolean;
    maxCommentLength: number;
    onRatingChange: (rating: number) => void;
    onCommentChange: (value: string) => void;
    onSubmit: () => void;
    onClose: () => void;
}

export function CandidateFeedbackModal({
    isOpen,
    variant,
    rating,
    comment,
    candidate,
    isSubmitting,
    maxCommentLength,
    onRatingChange,
    onCommentChange,
    onSubmit,
    onClose,
}: CandidateFeedbackModalProps) {
    const { t } = useTranslation('interview');

    if (!isOpen) {
        return null;
    }

    const isSubmitDisabled = variant === "form" && (!rating || isSubmitting);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-dark-invirtus/50 backdrop-blur-xs px-4"
            role="dialog"
            aria-modal="true"
            aria-label={t(variant === 'admin' ? 'feedback.admin_dialog_aria' : 'feedback.dialog_aria')}
        >
            <div className="w-full max-w-md rounded-lg bg-white px-10 py-10 text-center shadow-2xl border border-light-lila/40">
                <div className="flex justify-center">
                    <div className="flex h-20 w-20 items-center justify-center">
                        <img src="/logo/light-logo.png" alt="Invirtus" width={80} height={80} />
                    </div>
                </div>

                <div className="mt-6 space-y-2">
                    <p className="text-xs font-heading uppercase tracking-[0.4em] text-purple">
                        {variant === "form" ? t('feedback.kicker_form')
                            : variant === "success" ? t('feedback.kicker_success')
                                : t('feedback.kicker_admin')}
                    </p>
                    <h2 className="text-2xl font-heading font-bold text-dark-invirtus">
                        {variant === "form" ? t('feedback.form_title')
                            : variant === "success" ? t('feedback.success_title')
                                : candidate}
                    </h2>
                    <p className="text-sm text-dark-invirtus/70 font-body">
                        {variant === "form" ? t('feedback.form_description')
                            : variant === "success" ? t('feedback.success_description')
                                : ""}
                    </p>
                </div>

                {/* Candidate Form */}
                {variant === "form" && (
                    <>
                        <div
                            className="mt-8 flex items-center justify-center gap-3"
                            role="radiogroup"
                            aria-label={t('feedback.rating_aria')}
                        >
                            {starValues.map((value) => (
                                <Button
                                    key={value}
                                    type="icon"
                                    intent="muted"
                                    size="lg"
                                    onClick={() => onRatingChange(value)}
                                    className="!bg-white"
                                    icon={
                                        <Star
                                            className={`
                                                !h-10 !w-10 
                                                cursor-pointer 
                                                transition-transform transition-colors duration-300 ease-in-out 
                                                ${rating && value <= rating
                                                  ? "!text-yellow-300 !fill-yellow-300 drop-shadow-sm hover:scale-110"
                                                  : "!text-gray-300 !fill-gray-300 drop-shadow-sm !hover:!scale-110"
                                                }
                                                focus:!outline-none focus:!ring-2 focus:!ring-yellow-300 !rounded-full
                                              `}
                                        />
                                    }
                                />
                            ))}
                        </div>

                        <div className="mt-8 text-left">
                            <label
                                htmlFor="candidate-feedback-comment"
                                className="text-sm font-heading font-semibold text-dark-invirtus"
                            >
                                {t('feedback.comments_label')}
                            </label>
                            <textarea
                                id="candidate-feedback-comment"
                                value={comment}
                                onChange={(event) => onCommentChange(event.target.value)}
                                maxLength={maxCommentLength}
                                rows={4}
                                className="mt-2 w-full rounded-lg border border-light-lila/60 bg-white px-4 py-3 text-sm text-dark-invirtus placeholder:text-dark-invirtus/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple font-body"
                                placeholder={t('feedback.comments_placeholder')}
                            />
                            <p className="mt-1 text-right text-xs text-dark-invirtus/50">
                                {comment.length}/{maxCommentLength}
                            </p>
                        </div>
                    </>
                )}

                {/* Admin Read-Only View */}
                {variant === "admin" && (
                    <>
                        <div className="mt-8 flex items-center justify-center gap-3">
                            {starValues.map((value) => (
                                <Star
                                    key={value}
                                    className={`
                                        h-10 w-10 
                                        cursor-pointer 
                                        transition-transform transition-colors duration-300 ease-in-out 
                                        ${rating && value <= rating
                                          ? "text-yellow-300 fill-yellow-300 drop-shadow-md hover:scale-110"
                                          : "text-gray-300 fill-gray-300 drop-shadow-md hover:scale-110"
                                        }
                                        focus:outline-none focus:ring-2 focus:ring-yellow-300 rounded
                                      `}
                                />
                            ))}
                        </div>

                        {comment && (
                            <div className="mt-6 p-4 rounded-lg bg-gray-100 text-left">
                                <p className="text-sm text-dark-invirtus/80 whitespace-pre-wrap">
                                    {comment}
                                </p>
                            </div>
                        )}
                    </>
                )}

                <Button
                    htmlType="button"
                    disabled={isSubmitDisabled}
                    onClick={variant === "form" ? onSubmit : onClose}
                    label={variant === "form"
                        ? isSubmitting
                            ? t('feedback.submitting')
                            : t('feedback.submit')
                        : t('feedback.close')}
                    type="contained"
                    intent="secondary"
                    loading={variant === "form" && isSubmitting}
                    className="mt-4 w-full"
                />
            </div>
        </div>
    );
}

